import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface ScoringProfile {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  signalWeights: Record<string, number>;
  createdAt: string;
}

const ScoringProfilesPanel: React.FC = () => {
  const [profiles, setProfiles] = useState<ScoringProfile[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/scoring-profiles');
      setProfiles(res.data.data ?? []);
    } catch {
      toast.error('Failed to load scoring profiles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between gap-4 border-b border-slate-100 dark:border-white/5 pb-5">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/20 flex items-center justify-center text-indigo-500">
            <span className="material-symbols-outlined text-[24px]">tune</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Scoring Profiles</h2>
            <p className="text-[11px] text-slate-400 dark:text-slate-600 mt-0.5">
              Vertical-specific signal weight configurations. Default = Automotive Sales.
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 bg-amber-50/50 dark:bg-amber-950/10 border border-amber-100 dark:border-amber-900/20 rounded-2xl flex items-start gap-4">
        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center text-amber-600 shrink-0">
          <span className="material-symbols-outlined text-base">hourglass_top</span>
        </div>
        <p className="text-sm font-medium text-amber-700 dark:text-amber-400/80 leading-relaxed">
          Additional vertical profiles (Fleet Sales, Equipment Sales, Field Service) are coming soon.
          <span className="block mt-0.5 opacity-80 font-normal">
            The default Automotive Sales profile is active. Create and edit controls will be available once additional verticals are ready.
          </span>
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
        </div>
      ) : profiles.length === 0 ? (
        <p className="text-sm text-slate-400 dark:text-slate-600 py-6 text-center">
          No scoring profiles configured.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {profiles.map((p) => (
            <div
              key={p.id}
              className="p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-sm text-slate-900 dark:text-white">{p.name}</h3>
                {p.isDefault && (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-primary/10 text-primary">
                    Default
                  </span>
                )}
              </div>
              {p.description && (
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">{p.description}</p>
              )}
              <div className="space-y-1.5">
                {Object.entries(p.signalWeights).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-slate-500 w-14 uppercase tracking-widest">
                      {k}
                    </span>
                    <div className="flex-1 h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${v * 100}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-slate-500 w-10 text-right">
                      {(v * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ScoringProfilesPanel;
